import React, { useState, useRef, useMemo } from 'react';
import {
  Box,
  Card,
  CardMedia,
  IconButton,
  CircularProgress,
  Typography,
  Alert,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

/**
 * LogoUploadField - Campo de upload de logo com preview em avatar template
 * 
 * Props:
 * - logoUrl: string | null - URL do logo já salvo (para exibição/edição)
 * - onChange: (logoUrl: string) => void - Callback quando upload sucede
 * - pageShortId: string - ID curto da página para upload (usado como shortPageId)
 * - onError: (error: string) => void - Callback para erros
 * - isRound?: boolean - Se true, mostra avatar em formato circular; se false, em formato livre/original
 */
const LogoUploadField = ({ logoUrl, onChange, pageShortId, onError, isRound = false }) => {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(logoUrl || null);
  const fileInputRef = useRef(null);

  // Avatar container styles - muda conforme isRound
  const avatarContainerStyles = useMemo(() => {
    if (isRound) {
      return {
        width: 200,
        height: 200,
        borderRadius: '50%',
        overflow: 'hidden',
        flexShrink: 0,
      };
    } else {
      // Formato livre - mantém proporção original
      return {
        maxWidth: 300,
        maxHeight: 200,
        borderRadius: 2,
        overflow: 'hidden',
        flexShrink: 0,
      };
    }
  }, [isRound]);

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validação de tipo de arquivo
    if (!file.type.startsWith('image/')) {
      const error = 'Por favor, selecione um arquivo de imagem';
      onError?.(error);
      return;
    }

    // Validação de tamanho (máx 5MB)
    if (file.size > 5 * 1024 * 1024) {
      const error = 'Arquivo muito grande. Máximo 5MB';
      onError?.(error);
      return;
    }

    // Criar preview local imediatamente
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target?.result);
    };
    reader.readAsDataURL(file);

    // Fazer upload
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append('file', file);
      // NÃO enviar identificador de página: o FileService gera o id do arquivo e grava em
      // {userId}/logo/{fileId}. Mandar `shortPageId` indefinido virava a STRING "undefined" no
      // FormData e todos os logos da conta colidiam em {userId}/undefined.jpg (B-028).
      // `pageShortId` só é repassado quando o caller realmente fornece um valor.
      if (pageShortId) {
        formData.append('shortPageId', pageShortId);
      }

      // Obter token de autenticação (mesmo TOKEN_KEY do authService)
      const token = localStorage.getItem('agendario_jwt_token');
      
      if (!token) {
        throw new Error('Token de autenticação não encontrado. Faça login novamente.');
      }

      const uploadApiUrl = import.meta.env.VITE_UPLOAD_API_URL;
      if (!uploadApiUrl) {
        throw new Error('URL da API de upload não configurada');
      }

      const response = await fetch(
        `${uploadApiUrl}/api/logos`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Erro no upload: ${response.status}`);
      }

      const data = await response.json();
      const fileUrl = data.fileUrl;

      if (!fileUrl) {
        throw new Error('URL do arquivo não retornada');
      }

      // Chamar callback com a URL do logo
      onChange?.(fileUrl);
      console.log('[LogoUploadField] ✓ Upload bem-sucedido:', { fileUrl });
    } catch (error) {
      console.error('[LogoUploadField] Upload falhou:', error);
      setPreview(logoUrl || null); // Voltar ao preview anterior
      const errorMsg = error.message || 'Erro ao fazer upload do logo';
      onError?.(errorMsg);
    } finally {
      setLoading(false);
      // Limpar input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = () => {
    setPreview(null);
    onChange?.(null);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
        Avatar
      </Typography>

      {preview ? (
        // Avatar com preview
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            mb: 2,
          }}
        >
          {/* Avatar Frame */}
          <Box
            sx={{
              ...avatarContainerStyles,
              border: isRound ? '4px solid #e0e0e0' : '2px solid #e0e0e0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              position: 'relative',
              backgroundColor: '#fafafa',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={preview}
              alt="Avatar preview"
              style={{
                width: '100%',
                height: '100%',
                objectFit: isRound ? 'cover' : 'contain',
                padding: isRound ? 0 : 8,
              }}
            />
          </Box>

          {/* Delete Button */}
          <Tooltip title="Remover avatar">
            <IconButton
              size="small"
              onClick={handleRemove}
              color="error"
              disabled={loading}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        // Upload area
        <Card
          sx={{
            p: 3,
            textAlign: 'center',
            border: '2px dashed #ccc',
            borderRadius: 1,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1,
            transition: 'all 0.2s',
            mb: 2,
            '&:hover': !loading && { borderColor: '#1976d2', bgcolor: '#f5f5f5' },
          }}
          onClick={() => !loading && fileInputRef.current?.click()}
        >
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary">
                Enviando avatar...
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <CloudUploadIcon sx={{ fontSize: 40, color: '#ccc' }} />
              <Typography variant="body2" color="text.secondary">
                Clique para selecionar ou arraste uma imagem
              </Typography>
              <Typography variant="caption" color="text.secondary">
                PNG, JPG, GIF (máx 5MB)
              </Typography>
            </Box>
          )}
        </Card>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={handleFileSelect}
        disabled={loading}
      />

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {isRound 
          ? 'Formato circular - A imagem será cortada para se encaixar no círculo'
          : 'Formato livre - A imagem mantém sua proporção original'}
      </Typography>
    </Box>
  );
};

export default LogoUploadField;
